import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import { getFirebaseAuth } from './firebase'
import { setAuthToken, ApiError } from '@/lib/api-client'
import { postAuthSession, getUsersMe } from './api'
import { isStaffRole, type AdminUser } from './types'

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous'

interface SessionState {
  status: SessionStatus
  user: AdminUser | null
  firebaseUser: FirebaseUser | null
}

interface SessionContextValue extends SessionState {
  signIn: (email: string, password: string) => Promise<void>
  signOutSession: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, setState] = useState<SessionState>({
    status: 'loading',
    user: null,
    firebaseUser: null,
  })

  // Keep latest token in a ref so authedFetch always sees the newest value.
  const tokenRef = useRef<string | null>(null)

  useEffect(() => {
    const auth = getFirebaseAuth()
    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      if (!fbUser) {
        tokenRef.current = null
        setAuthToken(null)
        setState({ status: 'anonymous', user: null, firebaseUser: null })
        return
      }
      try {
        const token = await fbUser.getIdToken()
        tokenRef.current = token
        setAuthToken(token)
      } catch {
        tokenRef.current = null
        setAuthToken(null)
        setState({ status: 'anonymous', user: null, firebaseUser: null })
        return
      }

      // Fetch the server-side profile to learn the role. If the backend is
      // down we keep the firebase session but mark as anonymous so guards
      // redirect to login with a clear error.
      try {
        const user = await getUsersMe()
        if (!isStaffRole(user.rol)) {
          await signOut(auth)
          setState({ status: 'anonymous', user: null, firebaseUser: null })
          return
        }
        setState({ status: 'authenticated', user, firebaseUser: fbUser })
      } catch {
        setState({ status: 'anonymous', user: null, firebaseUser: fbUser })
      }
    })
    return unsub
  }, [])

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    const auth = getFirebaseAuth()
    const cred = await signInWithEmailAndPassword(auth, email, password)
    const token = await cred.user.getIdToken(true)
    tokenRef.current = token
    setAuthToken(token)
    try {
      const res = await postAuthSession(token)
      if (res.needsProfile === true) {
        await signOut(auth)
        throw new ApiError(
          403,
          'NOT_STAFF',
          'Esta cuenta no tiene permisos para acceder al panel.',
        )
      }
      const user = res as AdminUser
      if (!isStaffRole(user.rol)) {
        await signOut(auth)
        throw new ApiError(
          403,
          'NOT_STAFF',
          'Esta cuenta no tiene permisos para acceder al panel.',
        )
      }
      setState({ status: 'authenticated', user, firebaseUser: cred.user })
    } catch (err) {
      // Do not leak a half-authenticated session: on any server error
      // during login, sign out locally before surfacing the error.
      await signOut(auth).catch(() => undefined)
      throw err
    }
  }, [])

  const signOutSession = useCallback(async (): Promise<void> => {
    const auth = getFirebaseAuth()
    await signOut(auth)
  }, [])

  const value = useMemo<SessionContextValue>(
    () => ({ ...state, signIn, signOutSession }),
    [state, signIn, signOutSession],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession debe usarse dentro de SessionProvider')
  return ctx
}
